import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Link } from '@launchpad-ui/components';

import { DimensionType, UsageTab, type UsageTabKey } from '../../../../types';
import { usageTabConfig } from '../../../constants/usageTabConfig';
import { getUsageDisplayName } from '../../../utils/getUsageDisplayName';

import styles from './UsageDetailAlert.module.css';

type UsageDetailAlertProps = {
  usageTab: UsageTabKey;
  dimension?: DimensionType;
  hasUsageData: boolean;
};

function getStorageKey(usageTab: UsageTabKey): string {
  return `sdk-app-id-info-dismissed-${usageTab}`;
}

// Legacy key used before per-meter keys were introduced. Checked on the Service Connections
// tab so users who already dismissed the original alert don't see it again.
const LEGACY_STORAGE_KEY = 'sdk-app-id-info-dismissed';

export function UsageDetailAlert({ usageTab, dimension, hasUsageData }: UsageDetailAlertProps) {
  const storageKey = getStorageKey(usageTab);
  const [sdkAppIdInfoDismissed, sdkAppIdInfoStorage] = useStorageSlot(storageKey);
  const [legacyDismissed] = useStorageSlot(LEGACY_STORAGE_KEY);

  const alertConfig = usageTabConfig[usageTab].sdkAppIdAlert;
  const isDismissed =
    sdkAppIdInfoDismissed === 'true' || (usageTab === UsageTab.SERVICE_CONNECTIONS && legacyDismissed === 'true');

  if (!alertConfig || dimension !== DimensionType.SDK_APP_ID || isDismissed || !hasUsageData) {
    return null;
  }

  const { sdkDescription = msg('billing.sdkAppIdAlert.defaultSdkDescription'), extraNote } = alertConfig;
  const meterNoun = getUsageDisplayName(usageTab, 'plural');

  return (
    <Alert
      status="info"
      isDismissable
      onDismiss={() => sdkAppIdInfoStorage.set('true')}
      className={styles.sdkAppIdInfoAlert}
    >
      <span>
        <Message
          id="billing.sdkAppIdAlert.body"
          values={{
            sdkDescription,
            meterNoun,
            code: (chunks) => <code>{chunks}</code>,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
        {extraNote ? (
          <>
            <br />
            {extraNote}
          </>
        ) : null}{' '}
        <Link href="https://launchdarkly.com/docs/sdk/features/app-config" target="_blank">
          {msg('billing.sdkAppIdAlert.readMore')}
        </Link>
      </span>
    </Alert>
  );
}
